/*
 * Copyright 2026 Sony Corporation
 */

import type { CreativeLook, CreativeLookMode } from '@ips-device-verse/twin-service/schema';

/** Everything the preview's post-process chain needs, and nothing Babylon-shaped. */
export interface Grade {
  /** `ImageProcessingConfiguration.contrast`; 1 is neutral. */
  contrast: number;
  /** `ColorCurves` global tint and saturation. */
  hue: number;
  density: number;
  saturation: number;
  /** `ColorCurves` per-range exposure, -100..100. */
  highlights: number;
  midtones: number;
  shadows: number;
  /** `SharpenPostProcess.edgeAmount`, 0..1. */
  sharpen: number;
}

interface ModeShift {
  saturation?: number;
  contrast?: number;
  shadows?: number;
  hue?: number;
  density?: number;
  /** The device greys out colour on these, and so does the preview. */
  monochrome?: boolean;
}

/**
 * How each look shifts the picture before the adjustments are applied.
 *
 * A visual approximation, not a colorimetric match: the real looks are the
 * device's own tone and colour curves, which are not published. The point is
 * that changing the look changes the picture in the direction it should, so the
 * preview stays useful for framing and grading decisions.
 */
const MODES: Record<CreativeLookMode, ModeShift> = {
  ST: {},
  PT: { saturation: -8, contrast: -0.05 },
  NT: { saturation: -25, contrast: -0.15 },
  VV: { saturation: 30, contrast: 0.2 },
  VV2: { saturation: 45, contrast: 0.3 },
  FL: { saturation: 8, contrast: 0.25, hue: 30, density: 10 },
  IN: { saturation: -10, contrast: -0.1, shadows: 12 },
  SH: { saturation: 5, contrast: -0.2, shadows: 20 },
  BW: { monochrome: true },
  SE: { monochrome: true, hue: 35, density: 40 },
  FL2: { saturation: 12, contrast: 0.2, hue: 20, density: 12 },
  FL3: { saturation: 6, contrast: 0.15, hue: 210, density: 10 },
  // The device holds six user-defined looks; nothing reports what is in them.
  CustomLook1: {},
  CustomLook2: {},
  CustomLook3: {},
  CustomLook4: {},
  CustomLook5: {},
  CustomLook6: {},
};

const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

/** Sharper edges, but the range control only widens what counts as an edge. */
const sharpenFor = (sharpness: number, range: number) => (sharpness / 9) * 0.55 * (0.6 + (range / 5) * 0.4);

export const gradeFor = (look: Required<CreativeLook>): Grade => {
  const mode = MODES[look.mode] ?? {};
  const fade = look.fade / 9;

  return {
    contrast: clamp(
      1 + (look.contrast / 9) * 0.5 + (mode.contrast ?? 0) + (look.clarity / 9) * 0.1 - fade * 0.25,
      0.1,
      2
    ),
    hue: mode.hue ?? 0,
    density: mode.density ?? 0,
    // A mode the device renders in monochrome cannot be coloured back in.
    saturation: mode.monochrome ? -100 : clamp((look.saturation / 9) * 60 + (mode.saturation ?? 0), -100, 100),
    highlights: clamp((look.highlights / 9) * 40, -100, 100),
    midtones: clamp((look.clarity / 9) * 25, -100, 100),
    shadows: clamp((look.shadows / 9) * 40 + (mode.shadows ?? 0) + fade * 35, -100, 100),
    sharpen: clamp(sharpenFor(look.sharpness, look.sharpnessRange), 0, 1),
  };
};
